export type Holder = 'Você' | 'Esposa' | 'Conjunto';
export type CardStatus = 'open' | 'paid' | 'overdue' | 'upcoming' | 'no_entries';
export type CommitmentStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';
export type CommitmentType = 'invoice' | 'recurring' | 'installment' | 'income' | 'one_off';
export type EntryNature = 'avulso' | 'recorrente' | 'fatura' | 'parcela';
export type EntryType = 'receita' | 'despesa';
export type CategoryId =
  | 'alimentacao'
  | 'moradia'
  | 'transporte'
  | 'saude'
  | 'lazer'
  | 'educacao'
  | 'vestuario'
  | 'assinaturas'
  | 'investimentos'
  | 'pessoal'
  | 'presentes'
  | 'outros';

export interface CategoryDefinition {
  id: CategoryId;
  emoji: string;
  name: string;
  color: string;
}

export const CATEGORIES: CategoryDefinition[] = [
  { id: 'alimentacao', emoji: '🍽️', name: 'Alimentação', color: '#F59E0B' },
  { id: 'moradia', emoji: '🏠', name: 'Moradia', color: '#6366F1' },
  { id: 'transporte', emoji: '🚗', name: 'Transporte', color: '#3B82F6' },
  { id: 'saude', emoji: '💊', name: 'Saúde', color: '#10B981' },
  { id: 'lazer', emoji: '🎮', name: 'Lazer', color: '#8B5CF6' },
  { id: 'educacao', emoji: '📚', name: 'Educação', color: '#06B6D4' },
  { id: 'vestuario', emoji: '👕', name: 'Vestuário', color: '#EC4899' },
  { id: 'assinaturas', emoji: '📱', name: 'Assinaturas', color: '#14B8A6' },
  { id: 'investimentos', emoji: '📈', name: 'Investimentos', color: '#F97316' },
  { id: 'pessoal', emoji: '👤', name: 'Pessoal', color: '#A78BFA' },
  { id: 'presentes', emoji: '🎁', name: 'Presentes', color: '#F43F5E' },
  { id: 'outros', emoji: '📦', name: 'Outros', color: '#94A3B8' },
];

const CATEGORY_ALIASES: Record<string, CategoryId> = {
  casa: 'moradia',
  lar: 'moradia',
  mercado: 'alimentacao',
  supermercado: 'alimentacao',
  alimentação: 'alimentacao',
  transporte: 'transporte',
  saúde: 'saude',
  educacao: 'educacao',
  educação: 'educacao',
  roupas: 'vestuario',
  assinatura: 'assinaturas',
  investimento: 'investimentos',
  presente: 'presentes',
};

export function normalizeCategory(value?: string | null): CategoryId {
  const normalized = value?.trim().toLowerCase() ?? '';
  if (CATEGORIES.some(category => category.id === normalized)) return normalized as CategoryId;
  return CATEGORY_ALIASES[normalized] ?? 'outros';
}

export function categoryLabel(value?: string | null): string {
  const category = normalizeCategory(value);
  return CATEGORIES.find(item => item.id === category)?.name ?? 'Outros';
}

export interface House {
  id: string;
  name: string;
  currency: string;
  created_at: string;
}

export interface Participant {
  id: string;
  house_id: string;
  name: string;
  avatar_emoji: string;
  auth_user_id?: string;
}

export interface Card {
  id: string;
  house_id: string;
  name: string;
  last_digits?: string;
  holder: Holder;
  closing_day?: number;
  due_day?: number;
  color: string;
  is_active: boolean;
  created_at: string;
}

export interface Invoice {
  id: string;
  card_id: string;
  house_id: string;
  month: number;
  year: number;
  total_amount: number;
  due_date?: string;
  status: CardStatus;
  category?: CategoryId;
  paid_at?: string;
  created_at: string;
  card?: Card;
}

export interface RecurringExpense {
  id: string;
  house_id: string;
  name: string;
  category: string;
  amount?: number;
  is_fixed: boolean;
  due_day?: number;
  start_month?: number;
  start_year?: number;
  is_active: boolean;
  participant_id?: string;
  created_at: string;
}

export interface RecurringOccurrence {
  id: string;
  recurring_expense_id: string;
  house_id: string;
  month: number;
  year: number;
  amount?: number;
  due_date?: string;
  status: CommitmentStatus;
  paid_at?: string;
  recurring_expense?: RecurringExpense;
}

export interface RecurringIncome {
  id: string;
  house_id: string;
  name: string;
  amount: number;
  is_active: boolean;
  created_at: string;
}

export interface Income {
  id: string;
  house_id: string;
  participant_id?: string;
  name: string;
  amount: number;
  month: number;
  year: number;
  is_recurring: boolean;
  recurring_income_id?: string;
  received_at?: string;
  payment_method?: string;
  notes?: string;
  source?: string;
  source_message_id?: string;
  created_at: string;
}

export interface Installment {
  id: string;
  house_id: string;
  card_id?: string;
  name: string;
  holder: Holder;
  total_amount: number;
  installment_count: number;
  installment_amount: number;
  start_month: number;
  start_year: number;
  is_active: boolean;
  category?: CategoryId;
  created_at: string;
  card?: Card;
}

export interface InstallmentOccurrence {
  id: string;
  installment_id: string;
  house_id: string;
  installment_number: number;
  month: number;
  year: number;
  amount: number;
  status: CommitmentStatus;
  paid_at?: string;
  installment?: Installment;
}

export interface OneOffExpense {
  id: string;
  house_id: string;
  participant_id?: string;
  name: string;
  amount: number;
  due_date?: string;
  month: number;
  year: number;
  status: CommitmentStatus;
  category?: CategoryId;
  paid_at?: string;
  payment_method?: string;
  notes?: string;
  source?: string;
  source_message_id?: string;
  created_at: string;
}

// Unified commitment item for list display
export interface CommitmentItem {
  id: string;
  type: CommitmentType;
  name: string;
  amount: number;
  amountMissing?: boolean;
  dueDate?: string;
  status: CommitmentStatus;
  month: number;
  year: number;
  category?: CategoryId;
  meta?: any; // original object
}

export interface Account {
  id: string;
  house_id: string;
  name: string;
  type: 'corrente' | 'poupança' | 'investimento';
  balance: number;
  color: string;
  is_active: boolean;
  created_at: string;
}

export interface Budget {
  id: string;
  house_id: string;
  category: CategoryId;
  month: number;
  year: number;
  amount: number;
}

export interface Goal {
  id: string;
  house_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  target_date?: string;
  emoji: string;
  is_completed: boolean;
  is_active: boolean;
  created_at: string;
}

export interface MonthSummary {
  totalIncome: number;
  totalCommitments: number;
  freeBalance: number;
  committedPercent: number;
  upcomingItems: CommitmentItem[];
}

export interface MonthHistory {
  month: number;
  year: number;
  income: number;
  expenses: number;
}

export interface ReportData {
  history: MonthHistory[];
  commitments: CommitmentItem[];
}
